import { useState } from 'react'
import { TaskCard } from './TaskCard'
import { Badge } from '@/components/common/Badge'
import { cn } from '@/lib/utils'
import type { Task, TaskStatus } from '@/types'

interface KanbanColumn {
  id: TaskStatus
  title: string
  color: string
  headerColor: string
}

const COLUMNS: KanbanColumn[] = [
  { id: 'TODO', title: 'Da Fare', color: 'border-slate-300', headerColor: 'bg-slate-400' },
  { id: 'IN_PROGRESS', title: 'In Corso', color: 'border-primary-300', headerColor: 'bg-primary-500' },
  { id: 'IN_REVIEW', title: 'In Review', color: 'border-warning-300', headerColor: 'bg-warning-500' },
  { id: 'BLOCKED', title: 'Bloccati', color: 'border-danger-300', headerColor: 'bg-danger-500' },
  { id: 'DONE', title: 'Completati', color: 'border-success-300', headerColor: 'bg-success-500' },
]

interface TaskKanbanProps {
  tasks: Task[]
  onTaskMove?: (taskId: string, newStatus: TaskStatus) => void
  onTaskClick?: (task: Task, e?: React.MouseEvent) => void
  selectedIds?: Set<string>
  onToggleSelect?: (taskId: string) => void
  selectionMode?: boolean
  onBulkMove?: (taskIds: string[], newStatus: TaskStatus) => void
  onAssignClick?: ((taskId: string, e: React.MouseEvent) => void) | undefined
}

export function TaskKanban({ tasks, onTaskMove, onTaskClick, selectedIds, onToggleSelect, selectionMode = false, onBulkMove, onAssignClick }: TaskKanbanProps) {
  const [draggingTaskId, setDraggingTaskId] = useState<string | null>(null)
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null)
  const [dragCount, setDragCount] = useState(1)

  // Group tasks by status
  const tasksByStatus = COLUMNS.reduce<Record<TaskStatus, Task[]>>(
    (acc, col) => {
      acc[col.id] = tasks.filter((t) => t.status === col.id)
      return acc
    },
    { TODO: [], IN_PROGRESS: [], IN_REVIEW: [], BLOCKED: [], DONE: [], CANCELLED: [] }
  )

  function handleDragStart(e: React.DragEvent, taskId: string) {
    const isSelected = selectedIds?.has(taskId) ?? false
    const count = isSelected && selectedIds ? selectedIds.size : 1
    setDragCount(count)

    // Store whether this is a multi-drag
    e.dataTransfer.setData('taskId', taskId)
    e.dataTransfer.setData('isMulti', isSelected && count > 1 ? 'true' : 'false')
    setDraggingTaskId(taskId)

    // Custom drag image with count badge
    if (count > 1) {
      const ghost = document.createElement('div')
      ghost.style.cssText = 'position:absolute;top:-9999px;padding:8px 16px;background:#0084DB;color:#fff;border-radius:8px;font-size:13px;font-weight:600;pointer-events:none;'
      ghost.textContent = `${count} task`
      document.body.appendChild(ghost)
      e.dataTransfer.setDragImage(ghost, 40, 20)
      requestAnimationFrame(() => document.body.removeChild(ghost))
    }
  }

  function handleDragEnd() {
    setDraggingTaskId(null)
    setDragOverColumn(null)
    setDragCount(1)
  }

  function handleDragOver(e: React.DragEvent, columnId: TaskStatus) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverColumn(columnId)
  }

  function handleDragLeave() {
    setDragOverColumn(null)
  }

  function handleDrop(e: React.DragEvent, columnId: TaskStatus) {
    e.preventDefault()
    const taskId = e.dataTransfer.getData('taskId')
    const isMulti = e.dataTransfer.getData('isMulti') === 'true'

    if (taskId) {
      if (isMulti && selectedIds && selectedIds.size > 1 && onBulkMove) {
        // Multi-drag: move all selected tasks
        onBulkMove(Array.from(selectedIds), columnId)
      } else if (onTaskMove) {
        // Single drag
        onTaskMove(taskId, columnId)
      }
    }
    setDraggingTaskId(null)
    setDragOverColumn(null)
    setDragCount(1)
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory sm:snap-none">
      {COLUMNS.map((column) => {
        const columnTasks = tasksByStatus[column.id] ?? []
        const isDragOver = dragOverColumn === column.id

        return (
          <div
            key={column.id}
            className={cn(
              'flex flex-col flex-shrink-0 snap-start',
              'min-w-[85vw] max-w-[85vw] sm:min-w-[260px] sm:max-w-[280px]',
              'bg-slate-50 rounded-2xl border border-slate-200',
              'transition-colors',
              isDragOver && 'bg-primary-50 border-primary-300'
            )}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            {/* Column header */}
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200">
              <div className={cn('w-2 h-2 rounded-full flex-shrink-0', column.headerColor)} />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide flex-1">
                {column.title}
              </span>
              <Badge variant="neutral" size="sm" className="tabular-nums">{columnTasks.length}</Badge>
            </div>

            {/* Task list */}
            <div className="flex-1 p-2 space-y-2 min-h-[200px]">
              {columnTasks.map((task) => {
                const isTaskSelected = selectedIds?.has(task.id) ?? false
                // When dragging a selected group, dim all selected cards
                const isDimmed = draggingTaskId !== null && draggingTaskId !== task.id && isTaskSelected && dragCount > 1

                return (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id)}
                    onDragEnd={handleDragEnd}
                    className={cn(isDimmed && 'opacity-50')}
                  >
                    <TaskCard
                      task={task}
                      onClick={
                        selectionMode && onToggleSelect
                          ? () => onToggleSelect(task.id)
                          : onTaskClick
                          ? (e) => onTaskClick(task, e)
                          : undefined
                      }
                      dragging={draggingTaskId === task.id}
                      selected={isTaskSelected}
                      selectionMode={selectionMode}
                      onAssignClick={onAssignClick}
                    />
                  </div>
                )
              })}

              {/* Empty state */}
              {columnTasks.length === 0 && (
                <div
                  className={cn(
                    'h-full min-h-[80px] flex items-center justify-center',
                    'border-2 border-dashed border-slate-200 rounded-lg',
                    'text-xs text-slate-400',
                    isDragOver && 'border-primary-300 bg-primary-50'
                  )}
                >
                  {isDragOver ? (dragCount > 1 ? `Rilascia ${dragCount} task` : 'Rilascia qui') : 'Nessun task'}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
